'use client'

import { dateToString, formatTimeTo12Hour } from '@repo/sdk'
import { Button, Popover, PopoverContent, PopoverTrigger, useSidebar } from '@repo/ui/components'
import { cn } from '@repo/ui/lib'
import { CalendarDays } from 'lucide-react'
import { PropsWithChildren, useMemo } from 'react'

import { useDateTimeInput } from './context.tsx'

interface Props {
  className?: string
}

export const DateTimeInputPopover = ({ children, className }: PropsWithChildren<Props>) => {
  const {
    dateControl,
    openState: [open, setOpen],
    quickSelectState: [quickSelect],
    timeControl,
    timeState: [time],
  } = useDateTimeInput()

  const { isMobile } = useSidebar()

  const buttonText = useMemo(() => {
    if (typeof quickSelect === 'string') {
      switch (quickSelect) {
        case 'next-month':
          return 'Next month'
        case 'next-week':
          return 'Next week'
        case 'not-sure':
          return 'Not sure'
      }
    }

    const date = dateControl.field.value ? dateToString(dateControl.field.value) : undefined

    if (date && time) {
      if ((time.type === '24' && 'Anytime today') || (time.type === 'tbc' && 'To be confirmed'))
        return date

      const fromTime = timeControl.field.value?.from
        ? formatTimeTo12Hour(timeControl.field.value.from)
        : ''
      const toTime = timeControl.field.value?.to
        ? formatTimeTo12Hour(timeControl.field.value.to)
        : ''
      const timeRange = fromTime && toTime ? ` - ${toTime}` : toTime

      return `${date} ${fromTime}${timeRange}`
    }

    return 'DD/MM/YY, 08:00-16:00'
  }, [dateControl.field.value, timeControl.field.value, time])

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'justify-between font-normal',
            dateControl.field.value && 'text-black',
            className,
          )}
          variant={'outline'}
        >
          {buttonText}
          <CalendarDays />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={'start'}
        alignOffset={isMobile ? -65 : -100}
        avoidCollisions={!isMobile}
        className={
          'z-[1500] flex w-96 flex-col items-center gap-3 overflow-y-auto max-sm:gap-2 max-sm:p-2'
        }
        side={isMobile ? 'bottom' : 'right'}
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}
